import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { X, Plus, Minus, Ruler } from 'lucide-react';
import SizeTableGenerator from './SizeTableGenerator';
import FabricCareSelector from './FabricCareSelector';
import { sortSizeEntries } from '../lib/sizeSort';

interface ProductModalProps {
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  product?: any; // Düzenlenecek ürün
}

// Sabit mağaza listesi
const STORE_LIST = [
  { code: "B07", name: "AKMERKEZ MAĞAZA", city: "İstanbul" },
  { code: "B08", name: "TÜRKBÜKÜ MAĞAZA", city: "Muğla" },
  { code: "B09", name: "ALAÇATI RESORT MAĞAZA", city: "İzmir" },
  { code: "B10", name: "OPTİMUM MAĞAZA", city: "İstanbul" },
  { code: "B11", name: "HILLTOWN MAĞAZA", city: "İzmir" },
  { code: "B12", name: "EDİTİON RESORT MAĞAZA", city: "Muğla" },
  { code: "B13", name: "İSTİNYE PARK MAĞAZA", city: "İstanbul" },
  { code: "B2C", name: "ONLINE", city: "İstanbul" },
  { code: "S003", name: "ÇEŞME MAĞAZA", city: "İzmir" },
  { code: "S005", name: "PANORA MAĞAZA", city: "Ankara" },
  { code: "S004", name: "SUADİYE MAĞAZA", city: "İstanbul" },
  { code: "S001", name: "TEŞVİKİYE MAĞAZA", city: "İstanbul" }
];

// Varsayılan renk listesi (API çalışmazsa fallback)
const DEFAULT_COLOR_LIST = [
  { code: "001", name: "Siyah" },
  { code: "011", name: "Gri Melanj" },
  { code: "002", name: "Beyaz" },
  { code: "018", name: "Lacivert" },
  { code: "062", name: "Siyah/Beyaz" },
  { code: "008", name: "Gri" },
  { code: "004", name: "Ekru" },
  { code: "024", name: "Koyu Camel" },
  { code: "028", name: "Koyu Gri" },
  { code: "020", name: "Kırık Beyaz" },
  { code: "006", name: "Antrasit" },
  { code: "069", name: "Kahverengi" }
];

// Renk varyantı tipi
interface ColorVariant {
  code: string;
  name: string;
}

// Kategori tipi
interface Category {
  _id: string;
  type: 'main' | 'sub' | 'detail';
  code: string;
  name: string;
  productCodeParam?: string;
  isActive: boolean;
  sortOrder: number;
}

const ProductModal: React.FC<ProductModalProps> = ({ onClose, onSubmit, product }) => {
  const [isRPT, setIsRPT] = useState(product?.isRPT === true);
  const [loading, setLoading] = useState(false);
  const [showSizeTable, setShowSizeTable] = useState(false);
  const isEditing = !!product;
  
  // Renkler API'den çekilecek
  const [colorList, setColorList] = useState<{code: string; name: string}[]>(DEFAULT_COLOR_LIST);
  
  // Kategoriler API'den çekilecek
  const [categories, setCategories] = useState<Category[]>([]);
  
  // Renkleri ve kategorileri API'den yükle
  useEffect(() => {
    const loadColors = async () => {
      try {
        const response = await axios.get('/colors/active');
        if (response.data && response.data.length > 0) {
          setColorList(response.data.map((c: any) => ({ code: c.code, name: c.name })));
        }
      } catch (error) {
        console.log('Renkler yüklenemedi, varsayılan liste kullanılıyor');
        // Varsayılan listeyi kullan
      }
    };
    
    const loadCategories = async () => {
      try {
        const response = await axios.get('/categories/flat');
        if (response.data && response.data.length > 0) {
          // Sadece aktif kategorileri al
          setCategories(response.data.filter((c: Category) => c.isActive));
        }
      } catch (error) {
        console.log('Kategoriler yüklenemedi');
      }
    };
    
    loadColors();
    loadCategories();
  }, []);
  
  // Mevcut renkleri al - eski format ve yeni format uyumluluğu
  const getInitialColors = (): ColorVariant[] => {
    if (product?.productionInfo?.colors && product.productionInfo.colors.length > 0) {
      return product.productionInfo.colors;
    }
    // Eski format uyumluluğu
    if (product?.productionInfo?.colorCode && product?.productionInfo?.colorName) {
      return [{ code: product.productionInfo.colorCode, name: product.productionInfo.colorName }];
    }
    return [];
  };
  
  // Çoklu renk seçimi state'i
  const [selectedColors, setSelectedColors] = useState<ColorVariant[]>(getInitialColors());
  const [colorSearchTerm, setColorSearchTerm] = useState('');
  const [isColorDropdownOpen, setIsColorDropdownOpen] = useState(false);
  const colorDropdownRef = useRef<HTMLDivElement>(null);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorDropdownRef.current && !colorDropdownRef.current.contains(event.target as Node)) {
        setIsColorDropdownOpen(false);
        setColorSearchTerm('');
      }
    };

    if (isColorDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorDropdownOpen]);
  
  // Form verilerini başlat - düzenleme modunda mevcut verileri kullan
  const [formData, setFormData] = useState({
    productName: product?.productionInfo?.productName || '',
    modelCode: product?.productionInfo?.modelCode || '',
    manufacturer: product?.productionInfo?.manufacturer || '',
    colorCode: product?.productionInfo?.colorCode || '', // DEPRECATED - eski uyumluluk için
    colorName: product?.productionInfo?.colorName || '', // DEPRECATED - eski uyumluluk için
    mainCategory: product?.categoryInfo?.mainCategory || '',
    subCategory: product?.categoryInfo?.subCategory || '',
    detailCategory: product?.categoryInfo?.detailCategory || '',
    salePrice: product?.productionInfo?.salePrice ?? '',
    discountPrice: product?.productionInfo?.discountPrice ?? '',
    // Tekstil üretim alanları
    fabricInfo: product?.productionInfo?.fabricInfo || '',
    // Bakım bilgileri
    careHtml: ''
  });
  
  // Renk checkbox toggle fonksiyonu
  const toggleColor = (color: {code: string; name: string}) => {
    setSelectedColors(prev => {
      const exists = prev.find(c => c.code === color.code);
      if (exists) {
        return prev.filter(c => c.code !== color.code);
      } else {
        return [...prev, { code: color.code, name: color.name }];
      }
    });
  };
  
  const [sizes, setSizes] = useState<Record<string, number>>(
    product?.productionInfo?.sizeDistribution || {}
  );
  const [customSize, setCustomSize] = useState('');
  
  // Alakasyon bilgilerini düzenle
  const getAllocationsFromProduct = () => {
    if (!product?.productionInfo?.allocationQuantities) return {};
    
    const result: Record<string, { sizes: Record<string, number>; shippingDate: string }> = {};
    const allocationData = product.productionInfo.allocationQuantities;
    
    Object.entries(allocationData).forEach(([store, data]: [string, any]) => {
      if (data && typeof data === 'object' && data.sizes) {
        result[store] = {
          sizes: data.sizes || data,
          shippingDate: data.shippingDate || product.productionInfo.shippingDate || ''
        };
      }
    });
    
    return result;
  };
  
  const [allocations, setAllocations] = useState<Record<string, {
    sizes: Record<string, number>;
    shippingDate: string;
  }>>(getAllocationsFromProduct());
  
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedStoreDate, setSelectedStoreDate] = useState('');
  const [tempAllocationSizes, setTempAllocationSizes] = useState<Record<string, number>>({});
  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [sizeTableData, setSizeTableData] = useState<{
    productType: string;
    measurements: Record<string, Record<string, string>>;
    htmlCode: string;
  } | null>(product?.sizeTableInfo || null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);

    try {
      const productData = {
        isRPT,
        productionInfo: {
          ...product?.productionInfo,
          productName: formData.productName,
          modelCode: formData.modelCode || null,
          manufacturer: formData.manufacturer,
          // Eski format uyumluluğu için ilk renk de korunur.
          colorCode: selectedColors.length > 0 ? selectedColors[0].code : null,
          colorName: selectedColors.length > 0 ? selectedColors[0].name : null,
          colors: selectedColors.length > 0 ? selectedColors : null,
          salePrice: formData.salePrice !== '' ? parseFloat(formData.salePrice) : null,
          discountPrice: formData.discountPrice !== '' ? parseFloat(formData.discountPrice) : null,
          sizeDistribution: Object.fromEntries(sortSizeEntries(sizes)),
          fabricInfo: (formData.fabricInfo + (formData.careHtml ? '\n\n' + formData.careHtml : '')) || null,
          // Alakasyon düzenleme pasif; mevcut kayıtlar korunmalı.
          allocationQuantities: product?.productionInfo?.allocationQuantities ?? null
        },
        categoryInfo: {
          ...product?.categoryInfo,
          mainCategory: formData.mainCategory,
          subCategory: formData.subCategory || null,
          detailCategory: formData.detailCategory || null
        },
        sizeTableInfo: sizeTableData
      };

      await onSubmit(productData);
    } catch (error) {
      toast.error('Ürün eklenirken hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const updateSize = (size: string, quantity: number) => {
    const newQuantity = Math.max(0, quantity);
    
    setSizes(prev => ({
      ...prev,
      [size]: newQuantity
    }));
    
    // Alakasyonları kontrol et - eğer stok azaltıldıysa
    if (newQuantity < sizes[size]) {
      const totalAllocatedForSize = Object.values(allocations).reduce(
        (sum, alloc) => sum + (alloc.sizes[size] || 0), 0
      );
      
      // Alakasyon stok miktarından fazlaysa, alakasyonları orantılı olarak azalt
      if (totalAllocatedForSize > newQuantity) {
        const ratio = newQuantity / totalAllocatedForSize;
        const newAllocations = {...allocations};
        
        Object.keys(newAllocations).forEach(store => {
          const currentAlloc = newAllocations[store].sizes[size] || 0;
          newAllocations[store] = {
            ...newAllocations[store],
            sizes: {
              ...newAllocations[store].sizes,
              [size]: Math.floor(currentAlloc * ratio)
            }
          };
        });
        
        setAllocations(newAllocations);
      }
    }
  };

  const addCustomSize = () => {
    if (customSize.trim() && !sizes.hasOwnProperty(customSize.trim())) {
      setSizes(prev => ({
        ...prev,
        [customSize.trim()]: 0
      }));
      setCustomSize('');
    }
  };

  const removeSize = (size: string) => {
    setSizes(prev => {
      const newSizes = { ...prev };
      delete newSizes[size];
      return newSizes;
    });
    
    // Bu bedeni alakasyonlardan da kaldır
    setAllocations(prev => {
      const newAllocations = { ...prev };
      Object.keys(newAllocations).forEach(store => {
        delete newAllocations[store].sizes[size];
      });
      return newAllocations;
    });
  };

  const loadLetterSizes = () => {
    const letterSizes = {
      'XS': 0,
      'S': 0,
      'M': 0,
      'L': 0,
      'XL': 0,
      'XXL': 0
    };
    setSizes(letterSizes);
  };

  const loadNumberSizes = () => {
    const numberSizes = {
      '34': 0,
      '36': 0,
      '38': 0,
      '40': 0,
      '42': 0,
      '44': 0
    };
    setSizes(numberSizes);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <Card className="w-full h-full m-0 rounded-none overflow-hidden flex flex-col">
        {/* Header */}
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 border-b">
          <div>
            <CardTitle className="text-lg text-primary">
              {isEditing ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {isEditing ? 'Ürün bilgilerini güncelleyebilirsiniz' : 'Ürün bilgilerini eksiksiz doldurunuz'}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        {/* Body */}
        <CardContent className="flex-1 overflow-y-auto p-4">
          <form onSubmit={handleSubmit} className="space-y-4">

            <div className="rounded-md border border-input bg-muted/20 p-3">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={isRPT}
                  onChange={(e) => setIsRPT(e.target.checked)}
                  aria-describedby="rpt-description"
                  className="h-4 w-4 rounded border-input accent-primary"
                />
                RPT Ürün
              </label>
              <p id="rpt-description" className="mt-1 text-xs text-muted-foreground">
                Devam eden / tekrar üretim ürünleri için işaretleyin. Bu bilgi fiyat listesinde ve Excel çıktısında görünür.
              </p>
            </div>

                {/* Temel Bilgiler - İlk Satır */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <div>
                    <label htmlFor="product-name" className="text-xs font-medium">Ürün Adı *</label>
                    <input
                      id="product-name"
                      type="text"
                      value={formData.productName}
                      onChange={(e) => setFormData({...formData, productName: e.target.value})}
                      required
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Model Kodu</label>
                    <input
                      type="text"
                      value={formData.modelCode}
                      onChange={(e) => setFormData({...formData, modelCode: e.target.value})}
                      placeholder="Opsiyonel"
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-medium mb-2 block">
                      Renk Varyantları 
                      {selectedColors.length > 0 && (
                        <Badge variant="secondary" className="ml-2">{selectedColors.length} renk seçili</Badge>
                      )}
                    </label>
                    
                    {/* Seçilen Renkler - Her zaman göster */}
                    {selectedColors.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {selectedColors.map(color => (
                          <Badge 
                            key={color.code} 
                            variant="default" 
                            className="text-xs cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors"
                            onClick={() => toggleColor(color)}
                          >
                            {color.code} - {color.name}
                            <X className="w-3 h-3 ml-1" />
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Dropdown Select */}
                    <div className="relative" ref={colorDropdownRef}>
                      <div 
                        className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background cursor-pointer flex items-center justify-between"
                        onClick={() => setIsColorDropdownOpen(!isColorDropdownOpen)}
                      >
                        <span className="text-muted-foreground">
                          {selectedColors.length === 0 ? 'Renk seçmek için tıklayın...' : `${selectedColors.length} renk seçili`}
                        </span>
                        <svg 
                          className={`w-4 h-4 transition-transform ${isColorDropdownOpen ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {/* Dropdown Panel */}
                      {isColorDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-background border border-input rounded-md shadow-lg">
                          {/* Arama */}
                          <div className="p-2 border-b">
                            <input
                              type="text"
                              value={colorSearchTerm}
                              onChange={(e) => setColorSearchTerm(e.target.value)}
                              placeholder="Renk ara..."
                              className="w-full px-3 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          
                          {/* Renk Listesi */}
                          <div className="max-h-48 overflow-y-auto">
                            {colorList
                              .filter(color => 
                                color.code.toLowerCase().includes(colorSearchTerm.toLowerCase()) ||
                                color.name.toLowerCase().includes(colorSearchTerm.toLowerCase())
                              )
                              .map(color => {
                                const isSelected = selectedColors.some(c => c.code === color.code);
                                return (
                                  <div
                                    key={color.code}
                                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                                      isSelected 
                                        ? 'bg-primary/10 text-primary' 
                                        : 'hover:bg-muted/50'
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleColor(color);
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {}}
                                      className="w-4 h-4 rounded border-input text-primary focus:ring-primary pointer-events-none"
                                    />
                                    <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                                      {color.code}
                                    </span>
                                    <span className="text-sm flex-1">{color.name}</span>
                                    {isSelected && (
                                      <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>
                                );
                              })}
                            {colorList.filter(color => 
                              color.code.toLowerCase().includes(colorSearchTerm.toLowerCase()) ||
                              color.name.toLowerCase().includes(colorSearchTerm.toLowerCase())
                            ).length === 0 && (
                              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                                Renk bulunamadı
                              </div>
                            )}
                          </div>

                          {/* Alt Bar */}
                          <div className="p-2 border-t bg-muted/20 flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">
                              {colorList.length} renk mevcut
                            </span>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm" 
                              className="h-7 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsColorDropdownOpen(false);
                                setColorSearchTerm('');
                              }}
                            >
                              Kapat
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Üretici</label>
                    <input
                      type="text"
                      value={formData.manufacturer}
                      onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label htmlFor="product-main-category" className="text-xs font-medium">Ana Grup *</label>
                    <select
                      id="product-main-category"
                      value={formData.mainCategory}
                      onChange={(e) => setFormData({...formData, mainCategory: e.target.value})}
                      required
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Seçiniz</option>
                      {categories
                        .filter(c => c.type === 'main')
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map(cat => (
                          <option key={cat._id} value={cat.name}>{cat.name}</option>
                        ))
                      }
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Alt Grup</label>
                    <select
                      value={formData.subCategory}
                      onChange={(e) => setFormData({...formData, subCategory: e.target.value})}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Seçiniz</option>
                      {categories
                        .filter(c => c.type === 'sub')
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map(cat => (
                          <option key={cat._id} value={cat.name}>{cat.name}</option>
                        ))
                      }
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Detay Grup</label>
                    <select
                      value={formData.detailCategory}
                      onChange={(e) => setFormData({...formData, detailCategory: e.target.value})}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Seçiniz</option>
                      {categories
                        .filter(c => c.type === 'detail')
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map(cat => (
                          <option key={cat._id} value={cat.name}>{cat.name}</option>
                        ))
                      }
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Satış Fiyatı (PSF)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.salePrice}
                      onChange={(e) => setFormData({...formData, salePrice: e.target.value})}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">İndirimli Fiyat (İSF)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.discountPrice}
                      onChange={(e) => setFormData({...formData, discountPrice: e.target.value})}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>

                {/* Kumaş Teknik Bilgileri */}
                <div>
                  <label className="text-xs font-medium">Kumaş Teknik Bilgileri (Bakım sembolleri otomatik eklenecek)</label>
                  <textarea
                    value={formData.fabricInfo + (formData.careHtml ? '\n\n' + formData.careHtml : '')}
                    onChange={(e) => {
                      // Bakım HTML'ini çıkar, sadece kumaş bilgisini kaydet
                      let cleanValue = e.target.value;
                      if (formData.careHtml) {
                        cleanValue = cleanValue.replace(formData.careHtml, '').replace(/\n\n$/, '');
                      }
                      setFormData({...formData, fabricInfo: cleanValue});
                    }}
                    rows={5}
                    placeholder="Örn: %100 Pamuk, 180 gr/m², 150 cm en, Interlok örgü, Reaktif boya"
                    className="w-full px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                {/* Bakım Bilgileri (Ticimax için) */}
                <div className="mt-4">
                  <FabricCareSelector
                    value={formData.careHtml}
                    onChange={(html) => {
                      // ✅ SADECE FORM STATE GÜNCELLENİYOR
                      // ❌ TİCİMAX'A OTOMATIK GÖNDERİM YOK
                      // 💾 Kaydetmek için "Ürün Ekle" veya "Güncelle" butonuna basın
                      setFormData({ ...formData, careHtml: html });
                    }}
                  />
                </div>

                {/* Beden Dağılımı */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-muted-foreground">Beden Dağılımı</h3>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={loadLetterSizes}>
                        Harf Bedenler
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={loadNumberSizes}>
                        Sayı Bedenler
                      </Button>
                      {Object.keys(sizes).length > 0 && (
                        <Button 
                          type="button" 
                          variant={sizeTableData ? "default" : "outline"}
                          size="sm" 
                          onClick={() => setShowSizeTable(true)}
                        >
                          <Ruler className="w-4 h-4 mr-2" />
                          {sizeTableData ? (
                            <span className="flex items-center gap-1">
                              <span>Beden Tablosu Mevcut</span>
                              <span className="text-green-500">✓</span>
                            </span>
                          ) : 'Beden Tablosu Oluştur *'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Özel Beden Ekleme */}
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="text-xs font-medium">Özel Beden Ekle</label>
                      <input
                        type="text"
                        value={customSize}
                        onChange={(e) => setCustomSize(e.target.value.toUpperCase())}
                        placeholder="Örn: 3XL, 46, XS/S"
                        className="w-full px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomSize())}
                      />
                    </div>
                    <Button type="button" onClick={addCustomSize} disabled={!customSize.trim()}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Beden Listesi */}
                  <div className="border rounded-lg p-3 bg-muted/20">
                    {Object.keys(sizes).length === 0 ? (
                      <div className="text-center py-4 text-muted-foreground">
                        <p className="text-sm">Henüz beden eklenmedi</p>
                        <p className="text-xs">Yukarıdaki butonlardan hazır beden setlerini yükleyin veya özel beden ekleyin</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                        {sortSizeEntries(sizes).map(([size, quantity]) => (
                          <div key={size} className="border rounded-md p-2 bg-background">
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-xs font-medium">{size}</label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeSize(size)}
                                className="h-4 w-4 p-0 text-destructive hover:text-destructive"
                              >
                                <X className="h-2 w-2" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => updateSize(size, quantity - 1)}
                                disabled={quantity <= 0}
                                className="h-6 w-6 p-0"
                              >
                                <Minus className="h-2 w-2" />
                              </Button>
                              <input
                                type="number"
                                value={quantity}
                                onChange={(e) => updateSize(size, parseInt(e.target.value) || 0)}
                                className="w-14 px-1 py-0.5 text-center border border-input rounded text-xs"
                                min="0"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => updateSize(size, quantity + 1)}
                                className="h-6 w-6 p-0"
                              >
                                <Plus className="h-2 w-2" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Toplam Bilgi */}
                  {Object.keys(sizes).length > 0 && (
                    <div className="flex justify-between items-center text-xs bg-muted/50 p-2 rounded-md">
                      <span className="font-medium">
                        Toplam {Object.keys(sizes).length} beden
                      </span>
                      <span className="font-medium">
                        Toplam {Object.values(sizes).reduce((a, b) => a + b, 0)} adet
                      </span>
                    </div>
                  )}
                </div>

                {/* Alakasyon Dağılımı (Beden Bazlı) - Sevk Tarihi ile */}
                {/* ŞİMDİLİK PASİF - Neşe Panelinde Kullanılmayacak */}
                {false && (
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium">Alakasyon Dağılımı (Beden Bazlı)</label>
                    <p className="text-xs text-amber-600 mt-1">⚠️ Sevk tarihi bugünden önceki bir tarih olamaz</p>
                    <p className="text-xs text-muted-foreground mt-0.5">📌 Mağaza ve tarih seçtiğinizde beden girişleri otomatik açılır</p>
                  </div>
                  <div className="space-y-2 border rounded-lg p-2 bg-muted/20">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">Mağaza bazında beden dağılımını belirtin</p>
                      <p className="text-xs text-muted-foreground">
                        Stok: {Object.values(sizes).reduce((sum, qty) => sum + qty, 0)} adet | 
                        Dağıtılan: {Object.values(allocations).reduce((sum, alloc) => 
                          Object.values(alloc.sizes).reduce((s, qty) => s + qty, 0) + sum, 0
                        )} adet
                      </p>
                    </div>
                    
                    {/* Yeni Alakasyon Ekleme */}
                    <div className="space-y-3 pb-2 border-b">
                      {/* Mağaza ve Tarih Seçimi */}
                      {!showAllocationForm && Object.keys(sizes).length > 0 && (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <select
                              value={selectedStore}
                              onChange={(e) => {
                                setSelectedStore(e.target.value);
                                // Mağaza ve tarih seçiliyse otomatik formu aç
                                if (e.target.value && selectedStoreDate && !allocations[e.target.value]) {
                                  setShowAllocationForm(true);
                                  const initialSizes = Object.keys(sizes).reduce((acc, size) => {
                                    acc[size] = 0;
                                    return acc;
                                  }, {} as Record<string, number>);
                                  setTempAllocationSizes(initialSizes);
                                }
                              }}
                              className="flex-1 px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                              <option value="">Mağaza Seçin</option>
                              {STORE_LIST.filter(store => !allocations[`${store.code} - ${store.name}`]).map(store => (
                                <option key={store.code} value={`${store.code} - ${store.name}`}>
                                  {store.code} - {store.name} ({store.city})
                                </option>
                              ))}
                            </select>
                            <input
                              type="date"
                              value={selectedStoreDate}
                              onChange={(e) => {
                                const selectedDate = new Date(e.target.value);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                selectedDate.setHours(0, 0, 0, 0);
                                
                                if (selectedDate < today) {
                                  toast.error('Geçmiş tarih seçilemez! Lütfen bugün veya gelecek bir tarih seçin.');
                                  return;
                                }
                                
                                setSelectedStoreDate(e.target.value);
                                
                                // Mağaza ve tarih seçiliyse otomatik formu aç
                                if (selectedStore && e.target.value && !allocations[selectedStore]) {
                                  // Tarih kontrolü
                                  if (selectedDate < today) {
                                    toast.error('Geçmiş tarih için alakasyon eklenemez!');
                                    return;
                                  }
                                  
                                  setShowAllocationForm(true);
                                  const initialSizes = Object.keys(sizes).reduce((acc, size) => {
                                    acc[size] = 0;
                                    return acc;
                                  }, {} as Record<string, number>);
                                  setTempAllocationSizes(initialSizes);
                                }
                              }}
                              min={new Date().toISOString().split('T')[0]}
                              placeholder="Sevk Tarihi"
                              className="px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                          </div>
                          {selectedStore && !selectedStoreDate && (
                            <p className="text-xs text-muted-foreground">* Sevk tarihi seçiniz</p>
                          )}
                        </div>
                      )}

                      {/* Beden Adet Girişi */}
                      {showAllocationForm && (
                        <div className="space-y-3">
                          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <p className="text-sm font-medium text-blue-900">{selectedStore}</p>
                                <p className="text-xs text-blue-700">Sevk: {new Date(selectedStoreDate).toLocaleDateString('tr-TR')}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <p className="text-xs text-blue-700">Seçilen Adet</p>
                                  <p className="text-sm font-bold text-blue-900">{Object.values(tempAllocationSizes).reduce((sum, qty) => sum + qty, 0)}</p>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setShowAllocationForm(false);
                                    setTempAllocationSizes({});
                                    setSelectedStore('');
                                    setSelectedStoreDate('');
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                              {Object.keys(sizes).map(size => {
                                const totalAllocatedForSize = Object.entries(allocations)
                                  .reduce((sum, [_, alloc]) => sum + (alloc.sizes[size] || 0), 0);
                                const availableForSize = (sizes[size] || 0) - totalAllocatedForSize;
                                
                                return (
                                  <div key={size} className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">{size}</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max={availableForSize}
                                      value={tempAllocationSizes[size] || 0}
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value) || 0;
                                        if (value < 0) {
                                          toast.error('Negatif değer girilemez!');
                                          return;
                                        }
                                        
                                        if (value > availableForSize) {
                                          toast.error(`${size} bedeni için maksimum ${availableForSize} adet girebilirsiniz!`);
                                          return;
                                        }
                                        
                                        setTempAllocationSizes({
                                          ...tempAllocationSizes,
                                          [size]: value
                                        });
                                      }}
                                      className="w-full px-2 py-1 text-sm border border-input rounded focus:outline-none focus:ring-1 focus:ring-ring"
                                    />
                                    <p className="text-xs text-muted-foreground text-center">Kalan: {availableForSize}</p>
                                  </div>
                                );
                              })}
                            </div>
                            
                            <div className="flex gap-2 mt-3 pt-3 border-t">
                              <Button
                                type="button"
                                variant="default"
                                onClick={() => {
                                  // En az bir beden seçilmiş mi kontrol et
                                  const totalSelected = Object.values(tempAllocationSizes).reduce((sum, qty) => sum + qty, 0);
                                  if (totalSelected === 0) {
                                    toast.error('En az bir beden için adet girmelisiniz!');
                                    return;
                                  }
                                  
                                  // Alakasyonu ekle
                                  setAllocations({
                                    ...allocations, 
                                    [selectedStore]: {
                                      sizes: {...tempAllocationSizes},
                                      shippingDate: selectedStoreDate
                                    }
                                  });
                                  
                                  // Formu temizle ve kapat
                                  setShowAllocationForm(false);
                                  setTempAllocationSizes({});
                                  setSelectedStore('');
                                  setSelectedStoreDate('');
                                  toast.success('Alakasyon başarıyla eklendi!');
                                }}
                                className="flex-1"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Alakasyonu Ekle
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setShowAllocationForm(false);
                                  setTempAllocationSizes({});
                                  setSelectedStore('');
                                  setSelectedStoreDate('');
                                }}
                                className="flex-1"
                              >
                                İptal
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {Object.keys(sizes).length === 0 && (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        Önce beden dağılımını belirleyin
                      </div>
                    )}
                    
                    {/* Alakasyon Listesi */}
                    {Object.entries(allocations).map(([store, allocation]) => (
                      <div key={store} className="border rounded-md p-2 bg-background space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h5 className="font-medium text-xs">{store}</h5>
                            <span className="text-xs text-muted-foreground">
                              Sevk: {new Date(allocation.shippingDate).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newAllocations = {...allocations};
                              delete newAllocations[store];
                              setAllocations(newAllocations);
                            }}
                            className="h-5 w-5 p-0 text-destructive hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1">
                            {Object.keys(sizes).map((size) => {
                              // Tüm mağazaların bu bedendeki toplam alakasyonunu hesapla
                              const totalAllocatedForSize = Object.entries(allocations)
                                .filter(([s, _]) => s !== store)
                                .reduce((sum, [_, alloc]) => sum + (alloc.sizes[size] || 0), 0);
                              const availableForSize = (sizes[size] || 0) - totalAllocatedForSize;
                              const maxAllowed = Math.max(0, availableForSize);
                              
                              return (
                                <div key={size} className="flex items-center gap-1">
                                  <Badge variant="outline" className="text-xs px-1 py-0 min-w-[2rem] justify-center">{size}</Badge>
                                  <input
                                    type="number"
                                    min="0"
                                    max={maxAllowed}
                                    value={allocation.sizes[size] || 0}
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value) || 0;
                                      const newAllocations = {...allocations};
                                      newAllocations[store] = {
                                        ...newAllocations[store],
                                        sizes: {
                                          ...newAllocations[store].sizes,
                                          [size]: Math.min(Math.max(0, value), maxAllowed)
                                        }
                                      };
                                      setAllocations(newAllocations);
                                    }}
                                    title={`Max: ${maxAllowed} (Stok: ${sizes[size] || 0})`}
                                    className={`w-12 px-1 py-0.5 text-center border rounded text-xs ${
                                      allocation.sizes[size] > maxAllowed
                                        ? 'border-destructive text-destructive'
                                        : 'border-input'
                                    }`}
                                  />
                                </div>
                              );
                            })}
                        </div>
                        <div className="text-xs text-muted-foreground text-right">
                          Toplam: {Object.values(allocation.sizes).reduce((sum, qty) => sum + qty, 0)} adet
                        </div>
                      </div>
                    ))}
                    
                    {/* Genel Toplam */}
                    {Object.keys(allocations).length > 0 && (
                      <div className="pt-1 border-t">
                        <div className="flex justify-between text-xs font-medium">
                          <span>Genel Toplam Alakasyon:</span>
                          <span>{Object.values(allocations).reduce((sum, alloc) => 
                            sum + Object.values(alloc.sizes).reduce((s, q) => s + q, 0), 0
                          )} adet</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}
                {/* PASİF BÖLÜM SONU */}

            {/* Footer */}
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                İptal
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Kaydediliyor...' : (isEditing ? 'Güncelle' : 'Ürün Ekle')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      
      {/* Beden Tablosu Oluşturucu Modal */}
      {showSizeTable && (
        <SizeTableGenerator
          sizes={sizes}
          productName={formData.productName}
          required={false}
          existingData={sizeTableData}
          onClose={() => setShowSizeTable(false)}
          onSave={(data) => {
            setSizeTableData(data);
            setShowSizeTable(false);
            toast.success('Beden tablosu kaydedildi!');
          }}
        />
      )}
    </div>
  );
};

export default ProductModal;
